import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { locationTypes } from '../../constants/statusOptions';
import { createLocation } from '../../services/locationService';
import type { Location } from '../../types/location';

interface NewLocationDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (location: Location) => void;
  projectKey: string;
}

export const NewLocationDialog = ({
  open,
  onClose,
  onCreated,
  projectKey,
}: NewLocationDialogProps) => {
  const [locationId, setLocationId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [nameEditedByUser, setNameEditedByUser] = useState(false);
  const [locationType, setLocationType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isValid = locationId.trim() !== '' && locationName.trim() !== '' && locationType !== '';

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setError('');
    try {
      const created = await createLocation(projectKey, {
        LocationId: locationId.trim(),
        LocationName: locationName.trim(),
        LocationType: locationType,
      });
      onCreated(created);
      setLocationId('');
      setLocationName('');
      setNameEditedByUser(false);
      setLocationType('');
      setError('');
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to create location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Location</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Location ID"
            required
            fullWidth
            value={locationId}
            onChange={(e) => {
              const v = e.target.value; setLocationId(v);
              if (!nameEditedByUser) setLocationName(v);
            }}
          />
          <TextField
            label="Location Name"
            required
            fullWidth
            value={locationName}
            onChange={(e) => { setLocationName(e.target.value); setNameEditedByUser(true); }}
          />
          <TextField
            label="Location Type"
            required
            fullWidth
            select
            value={locationType}
            onChange={(e) => setLocationType(e.target.value)}
          >
            {locationTypes.map((lt) => (
              <MenuItem key={lt} value={lt}>{lt}</MenuItem>
            ))}
          </TextField>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || saving}
        >
          {saving ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
